import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Server, Database, Lock, Mail, Clock, FileText } from "lucide-react";

const LOGO_URL = "/images/logo-full.png";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" />
            <span>Torna al login</span>
          </Link>
          <img src={LOGO_URL} alt="Affidya" className="h-8" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Informativa sulla Privacy</h1>
              <p className="text-slate-500">Ai sensi del Regolamento UE 2016/679 (GDPR)</p>
            </div>
          </div>

          <p className="text-sm text-slate-500 mb-8">Ultimo aggiornamento: Dicembre 2025</p>

          {/* Sezione 1 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              1. Titolare del Trattamento
            </h2>
            <div className="bg-slate-50 rounded-lg p-4 text-slate-700">
              <p><strong>Titolare:</strong> R&CO. GROUP S.r.l.</p>
              <p><strong>Sede legale:</strong> Via Cicerone n. 60 - 00193 Roma</p>
              <p><strong>P.IVA:</strong> 18330111008</p>
              <p><strong>Email:</strong> privacy@affidya.it</p>
              <p><strong>PEC:</strong> recogroupsrl@sicurezzapostale.it</p>
            </div>
          </section>

          {/* Sezione 2 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              2. Dati Raccolti
            </h2>
            <p className="text-slate-700 mb-3">La piattaforma Affidya raccoglie e tratta le seguenti categorie di dati:</p>
            
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Dati della Struttura</h3>
                <p className="text-slate-600 text-sm">Ragione sociale, indirizzo, P.IVA, PEC, SDI, recapiti telefonici, sito web.</p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Dati del Referente/Utente</h3>
                <p className="text-slate-600 text-sm">Nome, cognome, email, telefono, ruolo, credenziali di accesso (password crittografata).</p>
              </div>
              
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <h3 className="font-medium text-amber-900 mb-2">Dati Particolari (Art. 9 GDPR)</h3>
                <p className="text-amber-800 text-sm">
                  <strong>Dati sanitari degli ospiti:</strong> anamnesi, patologie, terapie, referti medici, valutazioni funzionali, 
                  PAI (Piano Assistenziale Individualizzato), diario assistenziale.
                </p>
                <p className="text-amber-700 text-xs mt-2">
                  Questi dati sono inseriti esclusivamente dalla Struttura cliente in qualità di Titolare del trattamento 
                  per i propri ospiti.
                </p>
              </div>
            </div>
          </section>

          {/* Sezione 3 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              3. Finalità e Base Giuridica
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left p-3 font-medium">Finalità</th>
                    <th className="text-left p-3 font-medium">Base Giuridica</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="p-3">Erogazione del servizio gestionale</td>
                    <td className="p-3">Esecuzione contratto (Art. 6.1.b)</td>
                  </tr>
                  <tr>
                    <td className="p-3">Gestione account e autenticazione</td>
                    <td className="p-3">Esecuzione contratto (Art. 6.1.b)</td>
                  </tr>
                  <tr>
                    <td className="p-3">Assistenza tecnica e supporto</td>
                    <td className="p-3">Esecuzione contratto (Art. 6.1.b)</td>
                  </tr>
                  <tr>
                    <td className="p-3">Adempimenti fiscali e contabili</td>
                    <td className="p-3">Obbligo legale (Art. 6.1.c)</td>
                  </tr>
                  <tr>
                    <td className="p-3">Comunicazioni di servizio</td>
                    <td className="p-3">Legittimo interesse (Art. 6.1.f)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Sezione 4 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Server className="w-5 h-5 text-blue-600" />
              4. Luogo del Trattamento
            </h2>
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <p className="text-green-900 mb-3">
                <strong>I dati sono conservati esclusivamente all'interno dell'Unione Europea.</strong>
              </p>
              <div className="text-green-800 text-sm space-y-1">
                <p><strong>Infrastruttura:</strong> Google Cloud Platform</p>
                <p><strong>Regione:</strong> europe-west1 (Belgio, UE)</p>
                <p><strong>Backend:</strong> Google Compute Engine</p>
                <p><strong>Database:</strong> MongoDB su VM dedicata</p>
                <p><strong>Documenti:</strong> Google Cloud Storage (bucket privati)</p>
              </div>
            </div>
            <p className="text-slate-600 text-sm mt-3">
              Non vengono effettuati trasferimenti di dati verso paesi terzi al di fuori dello Spazio Economico Europeo.
            </p>
          </section>

          {/* Sezione 5 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              5. Conservazione dei Dati
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left p-3 font-medium">Tipologia Dati</th>
                    <th className="text-left p-3 font-medium">Periodo di Conservazione</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="p-3">Dati account utente</td>
                    <td className="p-3">Per tutta la durata del contratto + 10 anni</td>
                  </tr>
                  <tr>
                    <td className="p-3">Fascicoli assistenziali ospiti attivi</td>
                    <td className="p-3">Per tutta la durata della permanenza</td>
                  </tr>
                  <tr>
                    <td className="p-3">Fascicoli ospiti dimessi</td>
                    <td className="p-3">2 anni dalla dimissione (configurabile 1-10 anni)</td>
                  </tr>
                  <tr>
                    <td className="p-3">Documenti e referti medici</td>
                    <td className="p-3">Stessa retention del fascicolo associato</td>
                  </tr>
                  <tr>
                    <td className="p-3">Audit log (tracciamento accessi)</td>
                    <td className="p-3">Non cancellati di default (compliance)</td>
                  </tr>
                  <tr>
                    <td className="p-3">Dati di fatturazione</td>
                    <td className="p-3">10 anni (obbligo fiscale)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Sezione 6 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-600" />
              6. Misure di Sicurezza
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Autenticazione</h3>
                <ul className="text-slate-600 text-sm space-y-1">
                  <li>• Password con policy forte (10+ caratteri, complessità)</li>
                  <li>• Token JWT con scadenza 30 minuti</li>
                  <li>• Refresh token per sessioni prolungate</li>
                  <li>• Rate limiting su tentativi di login</li>
                </ul>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Crittografia</h3>
                <ul className="text-slate-600 text-sm space-y-1">
                  <li>• HTTPS/TLS per tutte le comunicazioni</li>
                  <li>• Password hashate con bcrypt</li>
                  <li>• Certificati SSL gestiti</li>
                </ul>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Controllo Accessi</h3>
                <ul className="text-slate-600 text-sm space-y-1">
                  <li>• RBAC (Role-Based Access Control)</li>
                  <li>• Tenant isolation (separazione dati clienti)</li>
                  <li>• Audit log di tutte le operazioni</li>
                </ul>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Infrastruttura</h3>
                <ul className="text-slate-600 text-sm space-y-1">
                  <li>• Firewall e VPC Google Cloud</li>
                  <li>• Backup regolari</li>
                  <li>• Monitoraggio continuo</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Sezione 7 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              7. Diritti dell'Interessato
            </h2>
            <p className="text-slate-700 mb-3">Ai sensi degli articoli 15-22 del GDPR, l'interessato ha diritto di:</p>
            <ul className="text-slate-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Accesso:</strong> ottenere conferma del trattamento e copia dei dati</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Rettifica:</strong> correggere dati inesatti o incompleti</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Cancellazione:</strong> richiedere la cancellazione dei dati ("diritto all'oblio")</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Limitazione:</strong> limitare il trattamento in determinati casi</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Portabilità:</strong> ricevere i dati in formato strutturato</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Opposizione:</strong> opporsi al trattamento per motivi legittimi</span>
              </li>
            </ul>
            <div className="mt-4 bg-blue-50 rounded-lg p-4">
              <p className="text-blue-900 text-sm">
                Per esercitare i tuoi diritti, contatta: <strong>privacy@affidya.it</strong>
              </p>
            </div>
          </section>

          {/* Sezione 8 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              8. Reclami
            </h2>
            <p className="text-slate-700">
              L'interessato ha diritto di proporre reclamo all'autorità di controllo competente:
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mt-3">
              <p><strong>Garante per la Protezione dei Dati Personali</strong></p>
              <p className="text-slate-600 text-sm">Piazza Venezia 11 - 00187 Roma</p>
              <p className="text-slate-600 text-sm">www.garanteprivacy.it</p>
            </div>
          </section>

          {/* Sezione 9 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">9. Modifiche all'Informativa</h2>
            <p className="text-slate-700">
              Il Titolare si riserva di modificare la presente informativa in qualsiasi momento. 
              Le modifiche saranno comunicate tramite avviso sulla piattaforma e/o via email.
              La versione aggiornata sarà sempre disponibile a questo indirizzo.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} R&CO. GROUP S.r.l. - Tutti i diritti riservati</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
            <Link to="/cookies" className="hover:text-slate-700">Cookie Policy</Link>
            <Link to="/terms" className="hover:text-slate-700">Termini di Servizio</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
